import {useShopId} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {useCurrencyConfig, useLocale} from '@aboutyou/i18n';
import type {
  ArticleDetailPageContext,
  BrandDetailPageContext,
  BrandDiscoverPageContext,
  CategoryDetailPageContext,
  CustomPageContext,
} from '@aboutyou/legacy-tracking-sdk';
import type {
  HelpCenterArticlePageContext,
  HelpCenterSectionPageContext,
  OrderSuccessPageContext,
  StoryClusterPageContext,
  StoryDetailPageContext,
} from '@aboutyou/legacy-tracking-sdk/src/ay-tracking/types/tracking-page';
import {
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
} from '@aboutyou/legacy-tracking-sdk/src/ay-tracking/types/tracking-page-types';
import {useCurrentPageType, usePathname, useSearch} from '@aboutyou/router';
import {
  GTMTrackingClientProvider,
  PageTrackingContextProvider,
} from '@aboutyou/tracking';
import React, {FC} from 'react';
import {useBasketFlyoutOpenedState} from '../backend-services/Basket/hooks/useBasketFlyoutOpenedState';
import {useBrandShopIdentifier} from '../backend-services/BrandHub/utils/useBrandShopIdentifier';
import {useCategoryId} from '../backend-services/CategoryStream/utils';
import {useCustomPageId} from '../backend-services/CustomPage/useCustomPageId';
import {useArticleIdFromUrl} from '../backend-services/HelpCenter/hooks/useArticleIdFromUrl';
import {useSectionIdFromUrl} from '../backend-services/HelpCenter/hooks/useSectionIdFromUrl';
import {useStoryClusterIdFromUrl} from '../backend-services/StoryClusterPage/useStoryClusterIdFromUrl';
import {useStoryIdFromUrl} from '../backend-services/StoryStream/utils/useStoryIdFromUrl';
import {useIsMiniAdpOpenFromBasketFlyout} from '../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {useProductIdFromSlug} from '../features/ProductUrl';
import {useSelectedTargetGroup} from '../shared-deprecated/useSelectedTargetGroup';

export const TrackingProvider: FC<
  React.PropsWithChildren<{documentReferrer: string}>
> = ({documentReferrer, children}) => {
  const shopId = useShopId();
  const search = useSearch();
  const pathname = usePathname();
  const pageTrackingInfo = useTrackingByPage();
  const locale = useLocale();
  const {iso} = useCurrencyConfig();
  const {gender} = useSelectedTargetGroup();

  return (
    <PageTrackingContextProvider
      path={pathname}
      search={search}
      {...pageTrackingInfo}
    >
      <GTMTrackingClientProvider
        shopIds={{shopId}}
        referrer={documentReferrer}
        locale={locale}
        currency={iso}
        search={search}
        shopGender={gender}
      >
        {children}
      </GTMTrackingClientProvider>
    </PageTrackingContextProvider>
  );
};

type TrackingPages =
  | ArticleDetailPageContext
  | BrandDetailPageContext
  | BrandDiscoverPageContext
  | CategoryDetailPageContext
  | OrderSuccessPageContext
  | CustomPageContext
  | StoryClusterPageContext
  | StoryDetailPageContext
  | HelpCenterArticlePageContext
  | HelpCenterSectionPageContext
  | {
      readonly type: TrackingPageTypeWithoutId;
      readonly id?: undefined;
    };

// eslint-disable-next-line complexity
const useTrackingByPage = (): TrackingPages => {
  // TODO: As soon as we add more pages we should rethink the approach of using multiple hooks for all pages. Maybe it will perform better if we only have one hook. https://aboutyou.atlassian.net/browse/ASFE-6527
  const categoryId = useCategoryId();
  const articleDetailId = useProductIdFromSlug();
  const brandId = useBrandShopIdentifier();
  const pageType = useCurrentPageType();
  const customPageId = useCustomPageId();
  const storyClusterId = useStoryClusterIdFromUrl();
  const storyId = useStoryIdFromUrl();
  const helpCenterArticleId = useArticleIdFromUrl();
  const helpCenterSectionId = useSectionIdFromUrl();
  const isBasketFlyoutOpen = useBasketFlyoutOpenedState().isOpen;
  const isMiniAdpOpenFromBasketFlyout = useIsMiniAdpOpenFromBasketFlyout();

  // ASFE-14251: when the new/semantic SRP is served (semantic-search rollout or the
  // `search_internationalization` A/B-test variant), the whole page is tracked as
  // `semantic_results` so that *all* feature events (e.g. add_to_basket_stream) carry
  // the same page_type as the content_view. Reading `.isActive` is passive — exposure
  // is activated explicitly in SearchPageResolver, not here.
  const isSemanticSearchEnabled = useIsFeatureActive(
    'enable_semantic_search',
  ).isActive;
  const isSearchAbTestActive = useIsFeatureActive(
    'search_internationalization',
  ).isActive;
  const shouldTrackSemanticResults =
    isSemanticSearchEnabled || isSearchAbTestActive;

  // ASFE-13005: the mini ADP opened from the basket flyout replaces the flyout on screen,
  // so it keeps reporting `basket_flyout` for content_view, add_to_cart and view_promotion.
  if (isBasketFlyoutOpen || isMiniAdpOpenFromBasketFlyout) {
    return {type: TrackingPageTypeWithoutId.BasketFlyout, id: undefined};
  }

  switch (pageType) {
    case 'category':
      return {
        id: categoryId as number,
        type: TrackingPageTypeWithId.Category,
      } as CategoryDetailPageContext;

    case 'articleDetail':
      return {
        id: articleDetailId,
        type: TrackingPageTypeWithId.ADP,
      } as ArticleDetailPageContext;

    case 'brandShop':
      return {
        id: brandId.id,
        type: TrackingPageTypeWithId.Brand,
      } as BrandDetailPageContext;

    case 'customPage':
      return {
        id: customPageId as number,
        type: TrackingPageTypeWithId.CustomPage,
      } as CustomPageContext;

    case 'storyCluster':
      return {
        type: TrackingPageTypeWithId.StoryCluster,
        id: storyClusterId,
      } as StoryClusterPageContext;

    case 'story':
      return {
        type: TrackingPageTypeWithId.Story,
        id: storyId as number,
      } as StoryDetailPageContext;

    case 'teaserPage':
      return {type: TrackingPageTypeWithoutId.Teaser};

    case 'basket':
      return {type: TrackingPageTypeWithoutId.Basket};

    case 'wishlist':
      return {type: TrackingPageTypeWithoutId.Wishlist};

    case 'similarProducts':
      return {type: TrackingPageTypeWithoutId.ProductRecoPage};

    case 'home':
      return {type: TrackingPageTypeWithoutId.Browse};

    case 'couponLandingPage':
      return {type: TrackingPageTypeWithoutId.CouponLandingPage};

    case 'streetwear':
      return {type: TrackingPageTypeWithoutId.Streetwear};

    case 'sportswear':
      return {type: TrackingPageTypeWithoutId.Sportswear};

    case 'orderSuccess':
      return {type: TrackingPageTypeWithoutId.OSP};

    case 'search':
      // `SemanticSearch` resolves to `semantic_results`, matching the content_view
      // override in SearchResultsMetaData.tsx so the whole page reports one page_type.
      return {
        type: shouldTrackSemanticResults
          ? TrackingPageTypeWithoutId.SemanticSearch
          : TrackingPageTypeWithoutId.Search,
      };

    case 'helpCenterLandingPageLegacy':
      return {type: TrackingPageTypeWithoutId.HelpCenter};

    case 'helpCenterSectionPageLegacy':
    case 'helpCenterSectionPage':
      if (helpCenterSectionId != null) {
        return {
          type: TrackingPageTypeWithId.HelpCenterSection,
          id: helpCenterSectionId,
        } as HelpCenterSectionPageContext;
      }
      return {type: TrackingPageTypeWithoutId.HelpCenter};

    case 'helpCenterArticlePageLegacy':
    case 'helpCenterArticlePage':
      return {
        type: TrackingPageTypeWithId.HelpCenterArticle,
        id: helpCenterArticleId ?? 0,
      } as HelpCenterArticlePageContext;

    case 'helpCenterSearchResultPageLegacy':
      return {type: TrackingPageTypeWithoutId.HelpCenterSearchResult};

    case 'contacts':
      return {type: TrackingPageTypeWithoutId.HelpCenterContact};

    default:
      return {type: TrackingPageTypeWithoutId.Other};
  }
};
